import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Ruler, Calendar, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Projets = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  
  // Fonction pour obtenir les études de cas traduites
  const getCaseStudies = () => {
    if (i18n.language === 'en') {
      return [
        {
          id: 1,
          title: "Residential Terrace in Stamped Concrete - Natural Stone Pattern",
          type: "Stamped Concrete",
          location: "Avignon, Vaucluse (84)",
          surface: "85 m²",
          date: "October 2024",
          client: "Individual",
          description: "Creation of a terrace for a detached house with natural stone pattern and sand shade. The project included land preparation, draining layer installation and application of UV-resistant protective resin.",
          challenge: "Sloped terrain requiring precise leveling and rainwater management.",
          solution: "Creation of a gentle 2% slope towards the garden with integration of expansion joints every 3 meters to prevent cracks.",
          duree: "5 days",
          image: "/images/projects/project_001.jpg"
        },
        {
          id: 2,
          title: "Pool Deck in Exposed Aggregate Concrete - Beige Aggregates",
          type: "Exposed Aggregate Concrete",
          location: "Carpentras, Vaucluse (84)",
          surface: "120 m²",
          date: "July 2024",
          client: "Individual",
          description: "Complete landscaping of pool surrounds with non-slip exposed aggregate concrete. Use of light beige rolled aggregates for a natural and elegant finish, treated with anti-chlorine resin.",
          challenge: "Area exposed to chlorinated water and intense UV from southern France.",
          solution: "Application of specific waterproof and UV treatment for pools, with non-slip matte finish.",
          duree: "6 days",
          image: "/images/projects/project_002.jpg"
        },
        {
          id: 3,
          title: "Driveway and Parking - Stamped Concrete Paver Pattern",
          type: "Stamped Concrete",
          location: "Orange, Vaucluse (84)",
          surface: "140 m²",
          date: "May 2024",
          client: "Individual",
          description: "Creation of a driveable alley with old paver pattern and contrasting borders. Reinforced slab thickness (15 cm) and adapted reinforcement to support vehicle passage.",
          challenge: "Daily circulation of 2 vehicles and need for hydrocarbon resistance.",
          solution: "High-strength concrete (dosed at 350 kg/m³) with double layer of anti-stain polyurethane resin.",
          duree: "7 days",
          image: "/images/projects/project_003.jpg"
        },
        {
          id: 4,
          title: "Commercial Outdoor Courtyard - Brushed Concrete",
          type: "Brushed Concrete",
          location: "Monteux, Vaucluse (84)",
          surface: "200 m²",
          date: "March 2024",
          client: "Business",
          description: "Complete renovation of a business outdoor courtyard with anti-slip brushed concrete. Driveable and aesthetic surface for customer welcome.",
          challenge: "Quick intervention between two business periods, without prolonged closure.",
          solution: "Organization in 2 phases allowing permanent access to the business, use of additives to accelerate setting.",
          duree: "9 days (2 phases)",
          image: "/images/projects/project_004.jpg"
        },
        {
          id: 5,
          title: "Multi-Level Terrace - Stamped Concrete Wood Effect",
          type: "Stamped Concrete",
          location: "Châteauneuf-du-Pape, Vaucluse (84)",
          surface: "95 m²",
          date: "September 2023",
          client: "Individual",
          description: "Two-level terrace with aged wood effect and nuanced Sienna earth tones. Harmonious integration with the wine-growing environment of the region.",
          challenge: "Create two distinct levels with integrated staircase, while maintaining visual harmony.",
          solution: "Precise coordination of patterns between levels and steps, use of 3 progressive shades for a natural effect.",
          duree: "6 days",
          image: "/images/projects/project_005.jpg"
        },
        {
          id: 6,
          title: "Pedestrian Entrance and Accessibility - Exposed Aggregate Concrete",
          type: "Exposed Aggregate Concrete",
          location: "Sorgues, Vaucluse (84)",
          surface: "75 m²",
          date: "February 2024",
          client: "Municipality",
          description: "Layout of a public entrance accessible to people with reduced mobility, with exposed aggregate concrete compliant with accessibility standards. Fine aggregates to facilitate wheelchair circulation.",
          challenge: "Strict compliance with accessibility standards (max 5% slope) and technical constraints.",
          solution: "Use of small caliber aggregates (6-10mm), optimized slope at 4%, and integrated guide strips.",
          duree: "5 days",
          image: "/images/projects/project_006.jpg"
        },
        {
          id: 7,
          title: "House Perimeter and Paths - Slate Stamped Concrete",
          type: "Stamped Concrete",
          location: "Avignon, Vaucluse (84)",
          surface: "160 m²",
          date: "June 2023",
          client: "Individual",
          description: "Complete layout around the house with slate-effect stamped concrete. Graphite gray tones for a contemporary and elegant look.",
          challenge: "Coordination with other trades and respect for existing plantings.",
          solution: "Work by zones with protective edges for plants, precise layout to respect existing levels.",
          duree: "8 days",
          image: "/images/projects/project_007.jpg"
        },
        {
          id: 8,
          title: "Hotel Terrace - Polished Concrete",
          type: "Polished Concrete",
          location: "L'Isle-sur-la-Sorgue, Vaucluse (84)",
          surface: "180 m²",
          date: "April 2023",
          client: "Hotel",
          description: "High-end terrace for a boutique hotel with polished concrete. Smooth and modern finish creating a refined atmosphere.",
          challenge: "Meet upscale aesthetic requirements while maintaining durability for hotel use.",
          solution: "Polished concrete with integrated tinting, multiple layers of protective sealer, matte finish.",
          duree: "7 days",
          image: "/images/projects/project_008.jpg"
        },
        {
          id: 9,
          title: "Sports Center Access - Exposed Aggregate Concrete",
          type: "Exposed Aggregate Concrete",
          location: "Vaison-la-Romaine, Vaucluse (84)",
          surface: "300 m²",
          date: "January 2023",
          client: "Municipality",
          description: "Access pathways and outdoor areas of a sports complex. Resistant exposed aggregate concrete adapted to intensive foot traffic.",
          challenge: "High foot traffic and need for ease of maintenance for a public facility.",
          solution: "Exposed aggregate concrete with hard aggregates, reinforced thickness, accessible routes compliant with standards.",
          duree: "10 days",
          image: "/images/projects/project_009.jpg"
        },
        {
          id: 10,
          title: "Private Garden Path - Stamped Concrete Flagstone Effect",
          type: "Stamped Concrete",
          location: "Bollène, Vaucluse (84)",
          surface: "55 m²",
          date: "November 2022",
          client: "Individual",
          description: "Winding garden path with irregular flagstone effect. Natural integration in wooded environment with ocher and beige tones.",
          challenge: "Irregular path with curves respecting existing trees.",
          solution: "Custom formwork following the design, flagstone pattern adapted to curves, progressive shades.",
          duree: "4 days",
          image: "/images/projects/project_010.jpg"
        }
      ];
    }
    
    // Version française (par défaut)
    return [
    {
      id: 1,
      title: "Terrasse Résidentielle en Béton Imprimé - Motif Pierre Naturelle",
      location: "Avignon, Vaucluse (84)",
      surface: "85 m²",
      date: "Octobre 2024",
      type: "Béton Imprimé",
      client: "Particulier",
      description: "Réalisation d'une terrasse pour une maison individuelle avec motif pierre naturelle et teinte sable. Le projet incluait la préparation du terrain, la pose d'un hérisson drainant et l'application d'une résine de protection anti-UV.",
      challenge: "Terrain en pente nécessitant un nivellement précis et une gestion des eaux pluviales.",
      solution: "Création d'une pente douce de 2% vers le jardin avec intégration de joints de dilatation tous les 3 mètres pour éviter les fissures.",
      duree: "5 jours",
      image: "/images/projects/project_001.jpg"
    },
    {
      id: 2,
      title: "Plage de Piscine en Béton Désactivé - Granulats Beige",
      location: "Carpentras, Vaucluse (84)",
      surface: "120 m²",
      date: "Juillet 2024",
      type: "Béton Désactivé",
      client: "Particulier",
      description: "Aménagement complet des abords de piscine avec béton désactivé antidérapant. Utilisation de granulats roulés beige clair pour un rendu naturel et élégant, traité avec une résine anti-chlore.",
      challenge: "Zone exposée à l'eau chlorée et aux UV intenses du sud de la France.",
      solution: "Application d'un traitement hydrofuge et anti-UV spécifique pour piscine, avec finition mate non glissante.",
      duree: "6 jours",
      image: "/images/projects/project_002.jpg"
    },
    {
      id: 3,
      title: "Allée de Garage et Parking - Béton Imprimé Motif Pavés",
      location: "Orange, Vaucluse (84)",
      surface: "140 m²",
      date: "Mai 2024",
      type: "Béton Imprimé",
      client: "Particulier",
      description: "Création d'une allée carrossable avec motif pavés anciens et bordures contrastées. Épaisseur de dalle renforcée (15 cm) et ferraillage adapté pour supporter le passage de véhicules.",
      challenge: "Circulation quotidienne de 2 véhicules et besoin de résistance aux hydrocarbures.",
      solution: "Béton haute résistance (dosé à 350 kg/m³) avec double couche de résine polyuréthane anti-taches.",
      duree: "7 jours",
      image: "/images/projects/project_003.jpg"
    },
    {
      id: 4,
      title: "Cour Extérieure Commercial - Béton Bâlé",
      location: "Monteux, Vaucluse (84)",
      surface: "200 m²",
      date: "Mars 2024",
      type: "Béton Bâlé",
      client: "Commerce",
      description: "Rénovation complète de la cour extérieure d'un commerce avec béton bâlé à texture antidérapante. Surface carrossable et esthétique pour accueil clientèle.",
      challenge: "Intervention rapide entre deux périodes d'activité commerciale, sans fermeture prolongée.",
      solution: "Organisation en 2 phases permettant l'accès permanent au commerce, utilisation d'adjuvants pour accélérer la prise.",
      duree: "9 jours (2 phases)",
      image: "/images/projects/project_004.jpg"
    },
    {
      id: 5,
      title: "Terrasse Multi-Niveaux - Béton Imprimé Aspect Bois",
      location: "Châteauneuf-du-Pape, Vaucluse (84)",
      surface: "95 m²",
      date: "Septembre 2023",
      type: "Béton Imprimé",
      client: "Particulier",
      description: "Terrasse sur 2 niveaux avec effet bois vieilli et teintes nuancées terre de Sienne. Intégration harmonieuse avec l'environnement viticole de la région.",
      challenge: "Créer deux niveaux distincts avec escalier intégré, tout en conservant l'harmonie visuelle.",
      solution: "Coordination précise des motifs entre niveaux et marches, utilisation de 3 teintes progressives pour un effet naturel.",
      duree: "6 jours",
      image: "/images/projects/project_005.jpg"
    },
    {
      id: 6,
      title: "Entrée Piétonne et Accès PMR - Béton Désactivé",
      location: "Sorgues, Vaucluse (84)",
      surface: "75 m²",
      date: "Février 2024",
      type: "Béton Désactivé",
      client: "Collectivité",
      description: "Aménagement d'une entrée publique accessible aux personnes à mobilité réduite, avec béton désactivé conforme aux normes PMR. Granulats fins pour faciliter la circulation des fauteuils roulants.",
      challenge: "Respect strict des normes d'accessibilité (pente max 5%) et contraintes techniques PMR.",
      solution: "Utilisation de granulats de petit calibre (6-10mm), pente optimisée à 4%, et bandes de guidage intégrées.",
      duree: "5 jours",
      image: "/images/projects/project_006.jpg"
    },
    {
      id: 7,
      title: "Contour de Maison et Chemins - Béton Imprimé Ardoise",
      location: "Avignon, Vaucluse (84)",
      surface: "160 m²",
      date: "Juin 2023",
      type: "Béton Imprimé",
      client: "Particulier",
      description: "Réalisation complète des abords de maison avec motif ardoise et teinte anthracite. Création de chemins, terrasse et zone de stationnement en harmonie visuelle.",
      challenge: "Surfaces multiples avec formes irrégulières et intégration paysagère.",
      solution: "Planification précise des zones, joints de séparation esthétiques entre espaces, et coordination avec paysagiste.",
      duree: "10 jours",
      image: "/images/projects/project_007.jpg"
    },
    {
      id: 8,
      title: "Parking Restaurant - Béton Désactivé Haute Résistance",
      location: "Orange, Vaucluse (84)",
      surface: "180 m²",
      date: "Novembre 2023",
      type: "Béton Désactivé",
      client: "Restaurant",
      description: "Parking clientèle pour restaurant avec béton désactivé haute résistance. Capacité 15 places de stationnement avec marquage intégré.",
      challenge: "Trafic intense quotidien et nécessité d'une résistance exceptionnelle à l'usure.",
      solution: "Béton armé de fibres métalliques, épaisseur 18 cm, granulats durs (porphyre) et double traitement de surface.",
      duree: "8 jours",
      image: "/images/projects/project_008.jpg"
    }
  ];
  };

  const caseStudies = getCaseStudies();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title={`${t('projects.title')} - DumaExpertBéton`}
        description={t('projects.intro1')}
        url="https://dumaexpertbeton.fr/projets"
      />
      
      {/* Header */}
      <div className="bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="flex justify-between items-start mb-4">
            <button onClick={handleBack} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700 transition-all duration-200 cursor-pointer">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">{t('common.back')}</span>
            </button>
            <LanguageSwitcher />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mt-4">{t('projects.title')}</h1>
          <p className="text-xl text-slate-300 mt-4">{t('projects.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <section className="mb-12 max-w-4xl mx-auto">
          <p className="text-xl text-slate-700 leading-relaxed mb-6">
            {t('projects.intro1')}
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            {t('projects.intro2')}
          </p>
        </section>

        {/* Stats rapides */}
        <section className="mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <Card className="text-center">
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-slate-900">2000+</p>
                <p className="text-sm text-slate-600 mt-1">{t('projects.projectsCompleted')}</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-slate-900">500 000m²+</p>
                <p className="text-sm text-slate-600 mt-1">{t('projects.surfaceTreated')}</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-slate-900">11 ans</p>
                <p className="text-sm text-slate-600 mt-1">{t('projects.experience')}</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-slate-900">65 km</p>
                <p className="text-sm text-slate-600 mt-1">{t('projects.actionRadius')}</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Études de cas */}
        <section className="space-y-12">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-slate-900">{t('projects.caseStudies')}</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              {t('projects.ourProjects')}
            </h2>
          </div>

          {getCaseStudies().map((project, index) => (
            <Card key={project.id} className="max-w-5xl mx-auto overflow-hidden border-2 border-slate-200">
              <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                  <div className="h-full min-h-[300px] bg-slate-200 flex items-center justify-center">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="flex flex-col items-center justify-center h-full text-slate-500"><svg class="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg><p class="text-sm">Photo du projet</p></div>';
                      }}
                    />
                  </div>
                </div>
                
                {/* Contenu */}
                <div className={`p-8 ${index % 2 === 1 ? 'md:col-start-1 md:row-start-1' : ''}`}>
                  <div className="mb-4">
                    <Badge className="mb-3">{project.type}</Badge>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      {project.title}
                    </h3>
                  </div>

                  {/* Métadonnées */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-slate-600" />
                      <span className="text-slate-700">{project.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Ruler className="w-4 h-4 text-slate-600" />
                      <span className="text-slate-700">{project.surface}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-slate-600" />
                      <span className="text-slate-700">{project.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-slate-600" />
                      <span className="text-slate-700">{project.duree}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{t('projects.description')}</h4>
                      <p className="text-slate-700 text-sm leading-relaxed">{project.description}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{t('projects.challenge')}</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{project.challenge}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{t('projects.solution')}</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{project.solution}</p>
                    </div>

                    <div className="pt-2">
                      <span className="inline-flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                        {t('projects.client')}: {project.client}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </section>

        {/* CTA */}
        <section className="mt-16 max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">{t('projects.ctaTitle')}</h3>
              <p className="text-xl text-slate-300 mb-8">
                {t('projects.ctaDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <button className="px-8 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors">
                    {t('projects.freeQuote')}
                  </button>
                </Link>
                <Link to="/rdv">
                  <button className="px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors">
                    {t('projects.bookAppointment')}
                  </button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default Projets;
